import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generateMetaTags, generateSchema, seoConfig } from '../../config/seo.config';

interface SEOProps {
  pageType: string;
  data?: any;
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: any[];
}

const SEO: React.FC<SEOProps> = ({
  pageType,
  data,
  title: customTitle,
  description: customDescription,
  keywords: customKeywords,
  canonicalUrl: customCanonicalUrl,
  ogTitle: customOgTitle,
  ogDescription: customOgDescription,
  ogImage: customOgImage,
  schema: customSchema,
}) => {
  // Generate meta tags from config
  const metaTags = generateMetaTags(pageType, data);
  
  // Use custom values if provided, otherwise use generated ones
  const title = customTitle || metaTags?.title || seoConfig.defaultTitle;
  const description = customDescription || metaTags?.description || seoConfig.defaultDescription;
  const keywords = customKeywords || (metaTags?.keywords ? metaTags.keywords.split(',').map((k: string) => k.trim()) : []);
  const canonicalUrl = customCanonicalUrl || metaTags?.ogUrl || seoConfig.baseUrl;
  const ogTitle = customOgTitle || metaTags?.ogTitle || title;
  const ogDescription = customOgDescription || metaTags?.ogDescription || description;
  const ogImage = customOgImage || metaTags?.ogImage || seoConfig.defaultImage;

  // Generate schema
  const generatedSchema = generateSchema(pageType, data);
  const schema = customSchema || (generatedSchema ? [generatedSchema] : []);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="1 days" />
      <meta name="author" content="Pincode Pro" />
      
      {/* Structured Data */}
      {schema.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}

      {/* Web App Manifest */}
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

export default SEO;
